import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { TIER_ORDER, bandLabel, inr, inrRange, priceBasisLabel, targetChip, type BriefEstimateInput, type FilmEstimate, type TierKey } from '../../lib/estimate';
import { Badge } from '../../ui/kit/components/primitives';

interface EstimateCardsProps {
  input: BriefEstimateInput | null;
  selected: TierKey | null;
  onSelect: (key: TierKey) => void;
  onEstimate: (estimate: FilmEstimate | null) => void;
}

export default function EstimateCards({ input, selected, onSelect, onEstimate }: EstimateCardsProps) {
  const [estimate, setEstimate] = useState<FilmEstimate | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const key = input ? JSON.stringify(input) : '';

  useEffect(() => {
    if (!input) { setEstimate(null); setState('idle'); onEstimate(null); return; }
    let cancelled = false;
    setState('loading');
    const timer = setTimeout(() => {
      api.estimateBrief(input)
        .then(result => { if (cancelled) return; setEstimate(result.estimate); setState('ready'); onEstimate(result.estimate); })
        .catch(() => { if (cancelled) return; setState('error'); onEstimate(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]);

  if (state === 'error') return <p className="bs-small bs-muted estimate-cards-note" role="status">The estimate is unavailable right now. You can still create the film; it appears after the story.</p>;
  if (!estimate) return <p className="bs-small bs-muted estimate-cards-note" role="status">{state === 'loading' ? 'Pricing three packages…' : 'Set the length and format to see what the film costs.'}</p>;

  const pick = selected || estimate.recommendedPackage;
  return <section className="estimate-cards" aria-label="Film cost estimate" aria-busy={state === 'loading'}>
    <div className="estimate-cards-head">
      <span className="bs-label">What this film costs · {bandLabel(estimate)}</span>
      <span className="bs-mono bs-muted estimate-basis">{priceBasisLabel(estimate.priceBasis)}</span>
    </div>
    <p className="bs-small bs-muted">{estimate.explain[0]}</p>
    <div className="estimate-cards-grid" role="radiogroup" aria-label="Production package">
      {TIER_ORDER.map(tier => {
        const pkg = estimate.packages[tier];
        const chip = targetChip(pkg);
        const active = pick === tier;
        return <button key={tier} type="button" role="radio" aria-checked={active} className={`estimate-card${active ? ' is-pick' : ''}${pkg.featured ? ' is-star' : ''}`} onClick={() => onSelect(tier)}>
          <span className="bs-label">{pkg.eyebrow}{pkg.featured ? ' · Director pick' : ''}</span>
          <b className="estimate-card-label">{pkg.label}</b>
          <span className="estimate-card-range bs-mono">{inrRange(pkg)}</span>
          <span className="bs-mono bs-muted bs-small">expected {inr(pkg.expectedINR)}</span>
          <span className="estimate-card-chips">
            <Badge>{bandLabel(estimate)}</Badge>
            {chip && <Badge tone={chip.over ? 'warning' : 'neutral'}>{chip.text}</Badge>}
          </span>
          <dl className="estimate-card-lines">
            {pkg.lines.map(line => <div key={line.key}><dt>{line.label}</dt><dd className="bs-mono">{line.key === 'audio' ? 'native' : inr(line.totalINR)}</dd></div>)}
          </dl>
          <span className="bs-mono bs-muted bs-small">{estimate.quantities.clips} clips × {estimate.quantities.typicalClipSeconds} s · {estimate.quantities.stills} stills · {pkg.models.resolution}</span>
          <span className="bs-small bs-muted">{pkg.modelLabels.llm} · {pkg.modelLabels.image} · {pkg.modelLabels.video}</span>
        </button>;
      })}
    </div>
  </section>;
}
